import type { ComponentDocs } from '../component-docs.js';
import type { CaosOpenRecord } from './open-records.js';

/**
 * What `<caos-open-records>` says about itself.
 *
 * IT OWNS NO STATE. The strip draws the `records` property and reports what the person did with it.
 * Which records are open, and which one you are looking at, is a routing question and belongs to the
 * shell. That is why the example declares a host: with nobody listening, a close control that never
 * closes anything and a label that never takes you anywhere is a working example teaching that the
 * piece does nothing. The old hand-kept example played the shell for the CLOSE; this one plays it
 * for the OPEN as well, because the label buttons are half the strip and they were inert.
 *
 * AN EMPTY LIST DRAWS NOTHING. The element stamps `has-records` on itself and a CSS rule collapses
 * it when the attribute is absent, so a workspace with nothing open shows no empty rail rather than
 * a hairline with nothing in it. There is deliberately no example of that state: an example that
 * draws nothing cannot be looked at.
 */
const docs: ComponentDocs = {
  aliases: ['record tabs', 'open tabs', 'tab bar', 'workspace tabs', 'recently opened strip', 'console tabs'],

  answers: [
    'hold several records open at once',
    'get back to the record I was looking at before this one',
    'show which of the open records is on screen',
    'close a record I have finished with',
  ],

  // A part of the workspace frame: one strip, directly under the app's tab row. A second one inside
  // a page would be a second claim about what is open, which is exactly what this is here to avoid.
  placements: ['seam'],

  inputs: [
    {
      name: 'records',
      // No `attribute`: a list of records cannot ride an attribute. It is set as a property.
      type: 'json',
      description:
        'The records currently open, in the order they were opened, as `{ id, label, href, active? }`. ' +
        '`label` is drawn as given — the shape used elsewhere is “OBJECT · record name”, composed by the ' +
        'caller, because this element knows nothing about the objects it is listing. `active` marks the ' +
        'one on screen; with none marked, the first entry still carries the keyboard tab stop.',
    },
  ],

  events: [
    {
      name: 'record-open',
      when: 'An entry’s label was chosen. The strip does not change what is active — the host does, by handing back a new list.',
      carries: '`{ id, href }` — which record, and the route to it.',
    },
    {
      name: 'record-close',
      when: 'An entry’s close control was used. The strip does not drop the entry — the host does.',
      carries: '`{ id, href }` — which record was closed.',
    },
  ],

  // No `<slot>` is rendered: every entry is built from the `records` property.
  state: [],

  slots: [],

  tokens: [
    { token: '--caos-color-surface', controls: 'The strip’s background band.' },
    { token: '--caos-color-border', controls: 'The rule under the strip and the hairline between one entry and the next.' },
    { token: '--caos-color-accent', controls: 'The underline beneath the entry you are currently looking at.' },
    { token: '--caos-color-text', controls: 'The active entry’s label, and any label under the pointer.' },
    { token: '--caos-color-text-muted', controls: 'A resting entry’s label.' },
    { token: '--caos-color-text-subtle', controls: 'The close control at rest (falls back to the muted text colour when unset).' },
    { token: '--caos-font-mono', controls: 'The typeface the entry labels are set in.' },
    { token: '--caos-radius-sm', controls: 'The corner radius of the focus ring on a label or a close control.' },
    { token: '--caos-space-2', controls: 'The gap between an entry’s label and its close control.' },
    { token: '--caos-space-3', controls: 'The padding at each end of an entry, and its height above and below the label.' },
  ],

  parts: [
    { part: 'strip', is: 'the strip the open records sit in' },
  ],

  methods: [],

  placing:
    'It goes directly under the app’s tab row, spanning the same width, as the second band of the workspace ' +
    'frame. What it needs around it is a host that owns the open set: something that adds a record when one ' +
    'is opened, marks the one being looked at, and removes one when its close control is used. It takes no ' +
    'vertical space at all while nothing is open.',

  whenToUse:
    'Only as the workspace frame’s strip of open records — the console idiom where opening a record does not ' +
    'throw away the one you were reading.',

  insteadUse: [
    { name: 'WorkspaceTabs', when: 'The row is the app’s destinations — objects, reports — rather than the records somebody happens to have open.' },
    { name: 'TabStrip', when: 'The choices are facets of ONE record: its details, its related lists, its history.' },
    { name: 'Breadcrumb', when: 'You want the way back up to a record’s parent rather than sideways to another open record.' },
  ],

  accessibility:
    'The strip labels itself “Open records” unless the author gives it another `aria-label`, and the entries ' +
    'share one tab stop with a roving tabindex — Arrow Left/Right, Home and End move between them, the WAI-ARIA ' +
    'pattern the record’s own tab strip uses. The active entry is marked `aria-current="page"`, each close ' +
    'control is a real button named “Close <label>” rather than a bare glyph, and both carry a focus ring. What ' +
    'is left to the author is the LABEL: it is what a screen reader reads and what the strip ellipsises at 22 ' +
    'characters, so it needs to say which record it is, not just which object.',

  examples: [
    {
      key: 'open-set',
      label: 'Three records open',
      summary:
        'A person holding an account, an order and a contact open at once, with the one on screen underlined ' +
        'in the accent.',
      placement: {
        component: 'caos-open-records',
        inputs: {
          records: [
            { id: 'a1', label: 'Meridian Supply', href: '/sales-atlas/account/a1', active: true },
            { id: 'o1', label: 'ORD-4471', href: '/sales-atlas/order/o1' },
            { id: 'c1', label: 'Dana Whitfield', href: '/sales-atlas/contact/c1' },
          ],
        },
      },
      host: {
        summary:
          'The surrounding page is the shell: closing an entry really drops the record, and choosing one really ' +
          'moves the underline to it — which is what the strip reports and the shell decides.',
        run: (stage) => {
          const strip = stage.querySelector('caos-open-records');
          if (!strip) return;
          let records: CaosOpenRecord[] = strip.records;
          strip.addEventListener('record-close', (e) => {
            const { id } = (e as CustomEvent<{ id: string }>).detail;
            records = records.filter((r) => r.id !== id);
            strip.records = records;
          });
          strip.addEventListener('record-open', (e) => {
            const { id } = (e as CustomEvent<{ id: string }>).detail;
            records = records.map((r) => ({ ...r, active: r.id === id }));
            strip.records = records;
          });
        },
      },
    },
  ],
};

export default docs;
